import React, { useState, useCallback, useMemo, useRef } from "react";
import {
    Modal,
    Select,
    Button,
    Table,
    Tag,
    Checkbox,
    Space,
    Alert,
    Descriptions,
    Typography,
    Divider,
    Spin,
    message,
} from "antd";
import {
    SwapOutlined,
    WarningOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import { useScheduleEditor } from "../context/ScheduleEditorContext";
import BatchAirportChangeService from "../services/BatchAirportChangeService";
import { flightRouteApi } from "../../../../api/flightRouteApi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const { Text } = Typography;
const { WARNING_TYPES } = BatchAirportChangeService;

const SIDE_OPTIONS = [
    { label: "出发和到达（两侧）", value: "both" },
    { label: "仅出发侧", value: "departure" },
    { label: "仅到达侧", value: "arrival" },
];

function parseSideValue(value) {
    if (value === "both") return ["departure", "arrival"];
    return [value];
}

const WARNING_LABELS = {
    [WARNING_TYPES.GROUND_TIME_SHORT]: { text: "过站不足", color: "orange" },
    [WARNING_TYPES.GROUND_TIME_CONFIG_MISSING]: { text: "过站配置缺失", color: "red" },
    [WARNING_TYPES.CURFEW]: { text: "触碰宵禁", color: "red" },
    [WARNING_TYPES.SAME_AIRPORT]: { text: "同场飞本场", color: "magenta" },
    [WARNING_TYPES.MISSING_ROUTE]: { text: "缺航路", color: "volcano" },
};

function formatUtcTime(utcStr) {
    if (!utcStr) return "-";
    const d = dayjs.utc(utcStr);
    return d.isValid() ? d.format("MM-DD HH:mm") : "-";
}

function formatBH(minutes) {
    if (!minutes && minutes !== 0) return "-";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h${String(m).padStart(2, "0")}m`;
}

const BatchAirportChangeModal = ({ open, onClose }) => {
    const { state } = useScheduleEditor();
    const { flights, schedule } = state;
    const aircraftReg = schedule?.aircraft?.registration;

    const [fromAirport, setFromAirport] = useState(null);
    const [toAirport, setToAirport] = useState(null);
    const [sideValue, setSideValue] = useState("both");
    const [skipUnmatchedRoutes, setSkipUnmatchedRoutes] = useState(false);
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState(null);

    const previewFlightsSnapshotRef = useRef(null);

    const airportOptions = useMemo(() => {
        const airports = window.cachedAirports || [];
        return airports
            .filter((a) => a.iata_code)
            .map((a) => ({
                label: `${a.iata_code} - ${a.name || a.iata_code}`,
                value: a.iata_code,
            }))
            .sort((a, b) => a.value.localeCompare(b.value));
    }, []);

    const usedAirports = useMemo(() => {
        const codes = new Set();
        for (const f of flights) {
            if (f.departure_airport) codes.add(f.departure_airport);
            if (f.arrival_airport) codes.add(f.arrival_airport);
        }
        return codes;
    }, [flights]);

    const fromAirportOptions = useMemo(() => {
        return airportOptions.filter((opt) => usedAirports.has(opt.value));
    }, [airportOptions, usedAirports]);

    const affectedCount = useMemo(() => {
        if (!fromAirport) return 0;
        return BatchAirportChangeService.findAffectedFlights(
            flights,
            fromAirport,
            parseSideValue(sideValue),
        ).length;
    }, [flights, fromAirport, sideValue]);

    const hasFlightsChanged = useCallback(() => {
        if (!previewFlightsSnapshotRef.current) return false;
        const current = flights.map((f) =>
            [f.id, f.departure_airport, f.arrival_airport, f.std, f.sta, f.flight_route_id].join("|"),
        ).join("||");
        return current !== previewFlightsSnapshotRef.current;
    }, [flights]);

    const handleGeneratePreview = useCallback(async () => {
        if (!fromAirport || !toAirport) {
            message.warning("请选择旧机场和新机场");
            return;
        }
        if (fromAirport === toAirport) {
            message.warning("新旧机场不能相同");
            return;
        }

        const sides = parseSideValue(sideValue);
        const bulkLegs = BatchAirportChangeService.buildBulkLegs(flights, fromAirport, toAirport, sides);

        if (bulkLegs.length === 0) {
            message.info("未找到可改航班");
            return;
        }

        setLoading(true);
        try {
            const resp = await flightRouteApi.getBulkBestMatchRoutes({
                aircraft_reg: aircraftReg || null,
                legs: bulkLegs,
            });
            const routeMap = resp?.data || {};

            const airportConstraints = state.constraints?.airportConstraints || {};

            const result = BatchAirportChangeService.generatePreview({
                flights,
                fromAirport,
                toAirport,
                sides,
                routeMap,
                airportConstraints,
                skipUnmatchedRoutes,
            });

            setPreview(result);

            previewFlightsSnapshotRef.current = flights
                .map((f) =>
                    [f.id, f.departure_airport, f.arrival_airport, f.std, f.sta, f.flight_route_id].join("|"),
                )
                .join("||");
        } catch (err) {
            console.error("生成预览失败:", err);
            message.error("航路匹配失败，请检查权限或网络");
        } finally {
            setLoading(false);
        }
    }, [fromAirport, toAirport, sideValue, flights, aircraftReg, state.constraints, skipUnmatchedRoutes]);

    const confirmDisabled = useMemo(() => {
        if (!preview) return true;
        if (hasFlightsChanged()) return true;
        return !BatchAirportChangeService.canConfirm(preview.summary, skipUnmatchedRoutes);
    }, [preview, skipUnmatchedRoutes, hasFlightsChanged]);

    const handleSkipChange = useCallback(
        (e) => {
            const checked = e.target.checked;
            setSkipUnmatchedRoutes(checked);
            if (preview) {
                setPreview(null);
            }
        },
        [preview],
    );

    const handleClose = useCallback(() => {
        setFromAirport(null);
        setToAirport(null);
        setSideValue("both");
        setSkipUnmatchedRoutes(false);
        setPreview(null);
        previewFlightsSnapshotRef.current = null;
        onClose(null);
    }, [onClose]);

    const handleConfirm = useCallback(() => {
        if (confirmDisabled) return;
        if (hasFlightsChanged()) {
            message.warning("预览后航班列表已被修改，请重新生成预览");
            setPreview(null);
            return;
        }
        onClose({
            preview,
            fromAirport,
            toAirport,
            sides: parseSideValue(sideValue),
            skipUnmatchedRoutes,
        });
        setFromAirport(null);
        setToAirport(null);
        setSideValue("both");
        setSkipUnmatchedRoutes(false);
        setPreview(null);
        previewFlightsSnapshotRef.current = null;
    }, [confirmDisabled, hasFlightsChanged, onClose, preview, fromAirport, toAirport, sideValue, skipUnmatchedRoutes]);

    const previewColumns = useMemo(
        () => [
            {
                title: "航班号",
                dataIndex: "flight_number",
                width: 90,
                fixed: "left",
            },
            {
                title: "日期",
                dataIndex: "flight_date",
                width: 80,
                render: (val) => formatUtcTime(val)?.slice(0, 5) || "-",
            },
            {
                title: "改前 出发-到达",
                key: "before_leg",
                width: 110,
                render: (_, row) => `${row.before.dep}-${row.before.arr}`,
            },
            {
                title: "改后 出发-到达",
                key: "after_leg",
                width: 110,
                render: (_, row) => {
                    if (row.skipped) return <Text type="secondary">跳过</Text>;
                    return (
                        <Text strong>
                            {row.after.dep}-{row.after.arr}
                        </Text>
                    );
                },
            },
            {
                title: "改后航路",
                key: "after_route",
                width: 140,
                ellipsis: true,
                render: (_, row) => {
                    if (row.skipped) return "-";
                    return row.after.route_name || <Text type="danger">无</Text>;
                },
            },
            {
                title: "轮档",
                key: "bh",
                width: 100,
                render: (_, row) => {
                    if (row.skipped) return "-";
                    const before = formatBH(row.before.bh);
                    const after = formatBH(row.after.bh);
                    if (before === after) return after;
                    return (
                        <span>
                            <Text delete type="secondary">{before}</Text>
                            {" → "}
                            <Text strong>{after}</Text>
                        </span>
                    );
                },
            },
            {
                title: "STD",
                key: "std",
                width: 100,
                render: (_, row) => formatUtcTime(row.after.std),
            },
            {
                title: "STA",
                key: "sta",
                width: 100,
                render: (_, row) => {
                    if (row.skipped) return formatUtcTime(row.before.sta);
                    const beforeSta = formatUtcTime(row.before.sta);
                    const afterSta = formatUtcTime(row.after.sta);
                    if (beforeSta === afterSta) return afterSta;
                    return (
                        <span>
                            <Text delete type="secondary">{beforeSta}</Text>
                            {" → "}
                            <Text strong>{afterSta}</Text>
                        </span>
                    );
                },
            },
            {
                title: "警告",
                key: "warnings",
                width: 180,
                render: (_, row) => {
                    if (!row.warnings?.length) {
                        return <Tag color="green" icon={<CheckCircleOutlined />}>正常</Tag>;
                    }
                    return (
                        <Space size={2} wrap>
                            {row.warnings.map((w) => {
                                const cfg = WARNING_LABELS[w];
                                return cfg ? (
                                    <Tag key={w} color={cfg.color} icon={<WarningOutlined />}>
                                        {cfg.text}
                                    </Tag>
                                ) : null;
                            })}
                        </Space>
                    );
                },
            },
        ],
        [],
    );

    const rowClassName = useCallback((record) => {
        if (record.skipped) return "batch-change-row-skipped";
        if (record.warnings?.includes(WARNING_TYPES.MISSING_ROUTE)) return "batch-change-row-error";
        if (record.warnings?.length > 0) return "batch-change-row-warning";
        return "";
    }, []);

    return (
        <Modal
            title={
                <Space>
                    <SwapOutlined />
                    批量改经停机场
                </Space>
            }
            open={open}
            onCancel={handleClose}
            width={1100}
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={handleClose}>取消</Button>
                    {!preview && (
                        <Button
                            type="primary"
                            onClick={handleGeneratePreview}
                            loading={loading}
                            disabled={!fromAirport || !toAirport || fromAirport === toAirport}
                        >
                            生成预览
                        </Button>
                    )}
                    {preview && (
                        <>
                            <Button onClick={handleGeneratePreview} loading={loading}>
                                重新预览
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleConfirm}
                                disabled={confirmDisabled}
                            >
                                确认写回
                            </Button>
                        </>
                    )}
                </Space>
            }
        >
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
                {/* 参数区 */}
                <Descriptions bordered size="small" column={3}>
                    <Descriptions.Item label="旧机场" span={1}>
                        <Select
                            showSearch
                            placeholder="选择要替换的机场"
                            options={fromAirportOptions}
                            value={fromAirport}
                            onChange={(val) => {
                                setFromAirport(val);
                                setPreview(null);
                            }}
                            style={{ width: "100%" }}
                            optionFilterProp="label"
                            disabled={loading}
                        />
                    </Descriptions.Item>
                    <Descriptions.Item label="新机场" span={1}>
                        <Select
                            showSearch
                            placeholder="选择目标机场"
                            options={airportOptions}
                            value={toAirport}
                            onChange={(val) => {
                                setToAirport(val);
                                setPreview(null);
                            }}
                            style={{ width: "100%" }}
                            optionFilterProp="label"
                            disabled={loading}
                        />
                    </Descriptions.Item>
                    <Descriptions.Item label="改哪侧" span={1}>
                        <Select
                            options={SIDE_OPTIONS}
                            value={sideValue}
                            onChange={(val) => {
                                setSideValue(val);
                                setPreview(null);
                            }}
                            style={{ width: "100%" }}
                            disabled={loading}
                        />
                    </Descriptions.Item>
                </Descriptions>

                {fromAirport && (
                    <Text type="secondary">
                        当前计划中有 <Text strong>{affectedCount}</Text> 个航班触碰 {fromAirport}
                    </Text>
                )}

                {/* 加载中 */}
                {loading && (
                    <div style={{ textAlign: "center", padding: 24 }}>
                        <Spin tip="正在匹配航路..." />
                    </div>
                )}

                {/* 预览 */}
                {preview && !loading && (
                    <>
                        <Divider style={{ margin: "8px 0" }} />

                        {/* 摘要 */}
                        <PreviewSummary summary={preview.summary} />

                        {/* 缺航路提示 */}
                        {preview.summary.missing_route_count > 0 && (
                            <Alert
                                type="warning"
                                icon={<ExclamationCircleOutlined />}
                                showIcon
                                message={
                                    <Space>
                                        <span>
                                            {preview.summary.missing_route_count} 个航班缺少航路，默认不可确认。
                                        </span>
                                        <Checkbox
                                            checked={skipUnmatchedRoutes}
                                            onChange={handleSkipChange}
                                        >
                                            跳过缺航路航班，只改其余
                                        </Checkbox>
                                    </Space>
                                }
                            />
                        )}

                        {/* 预览表格 */}
                        <Table
                            dataSource={preview.legs}
                            columns={previewColumns}
                            rowKey="flight_id"
                            size="small"
                            pagination={false}
                            scroll={{ x: 1000, y: 400 }}
                            rowClassName={rowClassName}
                        />

                        {hasFlightsChanged() && (
                            <Alert
                                type="error"
                                showIcon
                                message="预览后航班列表已被修改，请重新生成预览后再确认。"
                            />
                        )}
                    </>
                )}
            </Space>

            <style>{`
                .batch-change-row-skipped { opacity: 0.45; }
                .batch-change-row-error { background: #fff2f0 !important; }
                .batch-change-row-warning { background: #fffbe6 !important; }
            `}</style>
        </Modal>
    );
};

const PreviewSummary = ({ summary }) => {
    const items = [
        { label: "命中航班", value: summary.total_affected, color: undefined },
        { label: "已匹配航路", value: summary.matched_flight_count, color: "green" },
        { label: "跳过", value: summary.skipped_flight_count, color: summary.skipped_flight_count > 0 ? "orange" : undefined },
        { label: "缺航路", value: summary.missing_route_count, color: summary.missing_route_count > 0 ? "red" : undefined },
        { label: "过站警告", value: summary.ground_time_warning_count, color: summary.ground_time_warning_count > 0 ? "orange" : undefined },
        { label: "宵禁警告", value: summary.curfew_warning_count, color: summary.curfew_warning_count > 0 ? "red" : undefined },
        { label: "同场飞本场", value: summary.same_airport_warning_count, color: summary.same_airport_warning_count > 0 ? "magenta" : undefined },
    ];

    return (
        <Space size="middle" wrap>
            {items.map(({ label, value, color }) => (
                <span key={label}>
                    <Text type="secondary">{label}：</Text>
                    {color ? (
                        <Tag color={color}>{value}</Tag>
                    ) : (
                        <Text strong>{value}</Text>
                    )}
                </span>
            ))}
        </Space>
    );
};

export default BatchAirportChangeModal;
